import { useEffect, useState, useRef, useCallback } from "react";

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0,
      v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export interface IncomeMessage {
  id: string
  message: string
  is_request: boolean
  is_loading: boolean
}

const useWebSocket = (url: string, storageKey?: string) => {
  const [messages, setMessages] = useState<IncomeMessage[]>([]);
  const [isOnline, setIsOnline] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const isMountedRef = useRef(true);

  const OpenWS = useCallback(() => {
    const socket = new WebSocket(url);

    socket.onmessage = (event) => {
      if (!isMountedRef.current) return;
      
      try {
        const parsedData: IncomeMessage = JSON.parse(event.data);
        if (parsedData) {
          const msgs = (data: IncomeMessage[], new_msg: IncomeMessage) => {
            data = data.filter(message => message.id !== new_msg.id);
            return [...data, new_msg];
          }
          setMessages((prev) => msgs(prev, parsedData));
        }
      } catch (error) {
        console.error('Ошибка при парсинге JSON:', error);
      }
    };

    socket.onerror = (error) => {
      console.error('WebSocket ошибка:', error);
      if (isMountedRef.current) {
        setIsOnline(false);
      }
    };

    socket.onclose = () => {
      if (isMountedRef.current) {
        setIsOnline(false);
        console.log('WebSocket закрыт, попытаемся переподключиться...');
      }
    };

    socket.onopen = () => {
      if (isMountedRef.current) {
        setIsOnline(true);
      }
    };

    return socket;
  }, [url]);

  useEffect(() => {
    isMountedRef.current = true;
    // Load persisted messages when available
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed: IncomeMessage[] = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setMessages(parsed);
          }
        }
      } catch (e) {
        console.error('Failed to restore chat from storage:', e);
      }
    }
    socketRef.current = OpenWS();

    const checkConnection = setInterval(() => {
      if (!isMountedRef.current) return;
      
      if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
        console.log('Переподключение WebSocket...');
        socketRef.current = OpenWS();
      }
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearInterval(checkConnection);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      // Keep messages in memory for a smoother UX across quick mounts
      setIsOnline(false);
    };
  }, [url, storageKey]);

  // Persist messages when they change
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to persist chat to storage:', e);
    }
  }, [messages, storageKey]);

  const addMessage = useCallback((message: IncomeMessage) => {
    if (!isMountedRef.current) return;
    setMessages((prev) => [...prev, message]);
  }, []);

  // Replace current messages (e.g., with server history)
  const replaceMessages = useCallback((newMessages: IncomeMessage[]) => {
    if (!isMountedRef.current) return;
    setMessages(newMessages);
  }, []);

  const sendMessage = useCallback((message: string) => {
    if (!isMountedRef.current) return;
    
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      socketRef.current = OpenWS();
    }
    
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
    }
  }, [OpenWS]);

  const close = useCallback(() => {
    if (!isMountedRef.current) return;
    
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setIsOnline(false);
  }, []);

  return { messages, sendMessage, addMessage, replaceMessages, close, isOnline };
};

export default useWebSocket;
